import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Table,
  Card,
  Badge,
  Button,
  Form,
  Modal,
} from "react-bootstrap";
import { FaSearch, FaChevronDown, FaChevronUp, FaFilePdf, FaEdit, FaTrash } from "react-icons/fa";

import { siteReportsAPI } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import { toast } from "react-toastify";

const PAGE_SIZE = 25;

const SiteReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [dateAfter, setDateAfter] = useState("");
  const [dateBefore, setDateBefore] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const [expandedId, setExpandedId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [editingReport, setEditingReport] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: PAGE_SIZE,
        search,
        date_after: dateAfter || undefined,
        date_before: dateBefore || undefined,
        ordering: "-date",
      };

      const res = await siteReportsAPI.getSiteReports(params);

      setReports(res.data.results || []);
      setCount(res.data.count || 0);
      setExpandedId(null);
    } catch {
      toast.error("Failed to load site reports");
    } finally {
      setLoading(false);
    }
  }, [page, search, dateAfter, dateBefore]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const openEditModal = (report) => {
    setEditingReport(report);
    setEditForm({
      job_name: report.job_name,
      issue: report.issue,
      client_name: report.client_name,
      phone_number: report.phone_number,
      address: report.address,
      date: report.date,
      report_pdf: null,
    });
  };

  const closeEditModal = () => {
    setEditingReport(null);
    setEditForm(null);
  };

  const handleEditFieldChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingReport || !editForm) return;

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("job_name", editForm.job_name);
      formData.append("issue", editForm.issue);
      formData.append("client_name", editForm.client_name);
      formData.append("phone_number", editForm.phone_number);
      formData.append("address", editForm.address);
      formData.append("date", editForm.date);
      if (editForm.report_pdf) {
        formData.append("report_pdf", editForm.report_pdf);
      }

      await siteReportsAPI.updateSiteReport(editingReport.id, formData);
      toast.success("Site report updated");
      closeEditModal();
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update site report");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm(`Delete the site report "${report.job_name}" for ${report.client_name}? This cannot be undone.`)) {
      return;
    }

    try {
      await siteReportsAPI.deleteSiteReport(report.id);
      toast.success("Site report deleted");
      fetchReports();
    } catch {
      toast.error("Failed to delete site report");
    }
  };

  const totalPages = Math.ceil(count / PAGE_SIZE);

  if (loading) return <LoadingSpinner text="Loading site reports..." />;

  return (
    <Container fluid className="page-container">
      {/* HEADER */}
      <div className="mb-4">
        <div className={`d-flex align-items-center justify-content-between mb-2 ${isMobile ? "flex-column align-items-start" : ""}`}>
          <div className={`d-flex align-items-center ${isMobile ? "mb-2" : ""}`}>
            <h1 className="page-header-title mb-0 me-3">Site Reports</h1>
            <Badge bg="primary" className="fs-6">{count} Reports</Badge>
          </div>
        </div>
        <p className="page-header-subtitle text-muted mb-0">
          Site visit reports submitted from the field via the iOS app
        </p>
      </div>

      {/* SEARCH / FILTERS */}
      <Card className="mb-4 shadow-sm">
        <Card.Body className={`p-${isMobile ? "2" : "3"}`}>
          <Form.Group>
            <Form.Label className="fw-semibold">Search</Form.Label>
            <div className="position-relative">
              <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
              <Form.Control
                placeholder="Search by job, client, phone, or address..."
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                className="ps-5"
                size={isMobile ? "sm" : "md"}
              />
            </div>
          </Form.Group>

          <div className={`mt-3 d-grid ${isMobile ? "gap-2" : "gap-3"}`} style={{ gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
            <Form.Group>
              <Form.Label className="fw-semibold mb-1">Date from</Form.Label>
              <Form.Control
                type="date"
                value={dateAfter}
                onChange={(e) => {
                  setPage(1);
                  setDateAfter(e.target.value);
                }}
                size={isMobile ? "sm" : "md"}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="fw-semibold mb-1">Date to</Form.Label>
              <Form.Control
                type="date"
                value={dateBefore}
                onChange={(e) => {
                  setPage(1);
                  setDateBefore(e.target.value);
                }}
                size={isMobile ? "sm" : "md"}
              />
            </Form.Group>
          </div>
        </Card.Body>
      </Card>

      {/* CONTENT AREA */}
      <div className="position-relative">
        {loading && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75" style={{ zIndex: 10 }}>
            <LoadingSpinner text="Loading site reports..." />
          </div>
        )}

        {reports.length === 0 ? (
          <Card className="shadow-sm">
            <Card.Body className="text-center text-muted py-5">
              No site reports found.
            </Card.Body>
          </Card>
        ) : isMobile ? (
          reports.map((report) => {
            const expanded = expandedId === report.id;
            return (
              <Card key={report.id} className="mb-3 shadow-sm">
                <Card.Body
                  onClick={() => setExpandedId(expanded ? null : report.id)}
                  className="p-3"
                  style={{ cursor: "pointer" }}
                  tabIndex={0}
                  role="button"
                  aria-expanded={expanded}
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1 me-2">
                      <div className="fw-semibold text-truncate">{report.job_name}</div>
                      <div className="text-muted small text-truncate">{report.client_name} · {report.phone_number}</div>
                    </div>
                    <small className="text-muted">{report.date}</small>
                  </div>

                  {expanded && (
                    <div className="mt-3 pt-3 border-top">
                      <div className="mb-2">
                        <strong className="small text-muted d-block">Address</strong>
                        <div className="small">{report.address}</div>
                      </div>
                      <div className="mb-2">
                        <strong className="small text-muted d-block">Issue</strong>
                        <div className="small">{report.issue}</div>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {report.report_pdf_url && (
                          <a
                            href={report.report_pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FaFilePdf className="me-1" /> View report
                          </a>
                        )}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(report);
                          }}
                        >
                          <FaEdit className="me-1" /> Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(report);
                          }}
                        >
                          <FaTrash className="me-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            );
          })
        ) : (
          <Card className="shadow-sm">
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="border-0 fw-semibold">Job Name</th>
                    <th className="border-0 fw-semibold">Client</th>
                    <th className="border-0 fw-semibold">Phone</th>
                    <th className="border-0 fw-semibold">Address</th>
                    <th className="border-0 fw-semibold">Date</th>
                    <th className="border-0 fw-semibold">Report</th>
                    <th className="border-0 fw-semibold text-center">Actions</th>
                    <th className="border-0 fw-semibold text-center">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <React.Fragment key={report.id}>
                      <tr
                        className="align-middle"
                        onClick={() => setExpandedId(expandedId === report.id ? null : report.id)}
                        style={{ cursor: "pointer" }}
                        tabIndex={0}
                        role="button"
                        aria-expanded={expandedId === report.id}
                      >
                        <td className="fw-semibold">{report.job_name}</td>
                        <td>{report.client_name}</td>
                        <td className="font-monospace">{report.phone_number}</td>
                        <td className="text-truncate" style={{ maxWidth: "220px" }} title={report.address}>
                          {report.address}
                        </td>
                        <td>{report.date}</td>
                        <td>
                          {report.report_pdf_url ? (
                            <a
                              href={report.report_pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-outline-primary btn-sm"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FaFilePdf className="me-1" /> View
                            </a>
                          ) : (
                            <span className="text-muted small">No file</span>
                          )}
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(report);
                              }}
                              title="Edit"
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(report);
                              }}
                              title="Delete"
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                        <td className="text-center">
                          <Button variant="link" size="sm" className="p-0 text-decoration-none">
                            {expandedId === report.id ? (
                              <FaChevronUp className="text-primary" />
                            ) : (
                              <FaChevronDown className="text-muted" />
                            )}
                          </Button>
                        </td>
                      </tr>

                      {expandedId === report.id && (
                        <tr className="bg-light">
                          <td colSpan={8} className="border-0">
                            <Card className="border-0 shadow-none bg-transparent">
                              <Card.Body className="p-3">
                                <h6 className="text-primary mb-2">Issue</h6>
                                <p className="mb-0">{report.issue}</p>
                              </Card.Body>
                            </Card>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>
        )}

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <div className={`pagination-controls d-flex align-items-center ${isMobile ? "flex-column gap-2" : "gap-3"}`}>
              <Button
                variant="outline-primary"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                size={isMobile ? "sm" : "md"}
              >
                Previous
              </Button>
              <span className={`text-muted ${isMobile ? "text-center" : ""}`}>
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline-primary"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                size={isMobile ? "sm" : "md"}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* EDIT MODAL */}
      <Modal show={!!editingReport} onHide={closeEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Site Report</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveEdit}>
          <Modal.Body>
            {editForm && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Job Name</Form.Label>
                  <Form.Control
                    value={editForm.job_name}
                    onChange={(e) => handleEditFieldChange("job_name", e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Client Name</Form.Label>
                  <Form.Control
                    value={editForm.client_name}
                    onChange={(e) => handleEditFieldChange("client_name", e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Phone Number</Form.Label>
                  <Form.Control
                    value={editForm.phone_number}
                    onChange={(e) => handleEditFieldChange("phone_number", e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editForm.address}
                    onChange={(e) => handleEditFieldChange("address", e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={editForm.date}
                    onChange={(e) => handleEditFieldChange("date", e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Issue</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={editForm.issue}
                    onChange={(e) => handleEditFieldChange("issue", e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-1">
                  <Form.Label className="fw-semibold">Replace Report PDF (optional)</Form.Label>
                  <Form.Control
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => handleEditFieldChange("report_pdf", e.target.files[0] || null)}
                  />
                  <Form.Text className="text-muted">
                    Leave empty to keep the current file.
                  </Form.Text>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={closeEditModal} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default SiteReports;
